import AddBox from '@fuww/library/src/Icons/AddBox';
import Work from '@fuww/library/src/Icons/Work';
import Business from '@fuww/library/src/Icons/Business';
import ViewQuilt from '@fuww/library/src/Icons/ViewQuilt';
import PlaylistAddCheck from '@fuww/library/src/Icons/PlaylistAddCheck';
import Person from '@fuww/library/src/Icons/Person';
import CloudUpload from '@fuww/library/src/Icons/CloudUpload';
import PostAdd from '@fuww/library/src/Icons/PostAdd';
import Link from '@fuww/library/src/Icons/Link';
import ManageAccounts from '@fuww/library/src/Icons/ManageAccounts';
import MonetizationOn from '@fuww/library/src/Icons/MonetizationOn';
import PriceCheck from '@fuww/library/src/Icons/PriceCheck';
import ShoppingBag from '@fuww/library/src/Icons/ShoppingBag';
import Summarize from '@fuww/library/src/Icons/Summarize';
import Translate from '@fuww/library/src/Icons/Translate';
import Category from '@fuww/library/src/Icons/Category';
import MenuBook from '@fuww/library/src/Icons/MenuBook';
import People from '@fuww/library/src/Icons/People';
import Article from '@fuww/library/src/Icons/Article';
import PlaylistRemove from '@fuww/library/src/Icons/PlaylistRemove';
import CurrencyExchange from '@fuww/library/src/Icons/CurrencyExchange';
import SettingsApplications from '@fuww/library/src/Icons/SettingsApplications';
import Assessment from '@fuww/library/src/Icons/Assessment';
import Email from '@fuww/library/src/Icons/Email';
import Newspaper from '@fuww/library/src/Icons/Newspaper';
import QrCode2 from '@fuww/library/src/Icons/QrCode2';
import ConnectWithoutContact
  from '@fuww/library/src/Icons/ConnectWithoutContact';
import LocalOffer from '@fuww/library/src/Icons/LocalOffer';
import Image from '@fuww/library/src/Icons/Image';
import { ParsedUrlQuery } from 'node:querystring';
import QuestionMark from '@fuww/library/src/Icons/QuestionMark';
import Group from '@fuww/library/src/Icons/Group';
import ImageSearch from '@fuww/library/src/Icons/ImageSearch';
import Payments from '@fuww/library/src/Icons/Payments';
import AccountBalance from '@fuww/library/src/Icons/AccountBalance';
import { Roles } from '../components/UserProvider';
import { hasGlobalRole, hasRoleInAnyGroup } from '../lib/hasUserRoles';
import { getRouterState } from '../lib/savedRouterState';

interface DashboardNavigationItem {
  name: string;
  icon: React.ElementType;
  route?: string;
  parameters?: ParsedUrlQuery;
  href?: string;
  target?: string;
  subItems?: DashboardNavigationItem[];
}

const getDashboardNavigation = (
  userRoles: Roles,
): DashboardNavigationItem[] => {
  const dashboardJobsQuery = { ...getRouterState('dashboardJobs')?.query };

  const potentialItems = [
    hasRoleInAnyGroup(userRoles, 'jobs') ? {
      name: 'Jobs',
      icon: Work,
      route: 'dashboardJobs',
      parameters: dashboardJobsQuery,
    } : null,
    hasRoleInAnyGroup(userRoles, 'applicants') ? {
      name: 'Applicants',
      icon: Group,
      route: 'candidates',
    } : null,
    hasRoleInAnyGroup(userRoles, 'createLogo') ? {
      name: 'Upload',
      icon: CloudUpload,
      route: 'logo-form',
    } : null,
    hasGlobalRole(userRoles, 'createNewsArticle')
    || hasGlobalRole(userRoles, 'createPressNewsArticle') ? {
        name: 'Post an article',
        icon: PostAdd,
        route: 'news-form',
      } : null,
    hasRoleInAnyGroup(userRoles, 'updateNewsArticle') ? {
      name: 'News',
      icon: Newspaper,
      route: 'retool',
      parameters: {
        retoolPath: 'news',
      },
    } : null,
    hasRoleInAnyGroup(userRoles, 'accounts')
    || hasGlobalRole(userRoles, 'profiles')
    || hasGlobalRole(userRoles, 'users')
    || hasGlobalRole(userRoles, 'marketAccessRequests')
    || hasGlobalRole(userRoles, 'addBrandToProfile')
    || hasGlobalRole(userRoles, 'updateBrand')
    || hasGlobalRole(userRoles, 'updateTag')
    || hasGlobalRole(userRoles, 'categoryKeywords')
    || hasGlobalRole(userRoles, 'updateSpecialPage')
    || hasGlobalRole(userRoles, 'createNewsletter')
    || hasGlobalRole(userRoles, 'removeImage')
    || hasGlobalRole(userRoles, 'createAuthor')
      ? {
        name: 'Settings',
        icon: SettingsApplications,
        subItems: [
          hasRoleInAnyGroup(userRoles, 'accounts') ? {
            name: 'Credits',
            icon: MonetizationOn,
            route: 'retool',
            parameters: {
              retoolPath: 'credits',
            },
          } : null,
          hasGlobalRole(userRoles, 'accounts') ? {
            name: 'Accounts',
            icon: PriceCheck,
            route: 'retool',
            parameters: {
              retoolPath: 'accounts',
            },
          } : null,
          hasGlobalRole(userRoles, 'profiles') ? {
            name: 'Profiles',
            icon: Business,
            route: 'profiles-list',
          } : null,
          hasGlobalRole(userRoles, 'users') ? {
            name: 'Users',
            icon: Person,
            route: 'retool',
            parameters: {
              retoolPath: 'users',
            },
          } : null,
          hasGlobalRole(userRoles, 'marketAccessRequests') ? {
            name: 'Access requests',
            icon: ManageAccounts,
            route: 'access-requests',
          } : null,
          hasGlobalRole(userRoles, 'addBrandToProfile') ? {
            name: 'Brands & profiles',
            icon: Link,
            route: 'profiles-brands',
          } : null,
          hasGlobalRole(userRoles, 'updateBrand') ? {
            name: 'Brands',
            icon: ShoppingBag,
            route: 'retool',
            parameters: {
              retoolPath: 'brands',
            },
          } : null,
          hasGlobalRole(userRoles, 'updateTag') ? {
            name: 'Tags',
            icon: LocalOffer,
            route: 'retool',
            parameters: {
              retoolPath: 'tags',
            },
          } : null,
          hasGlobalRole(userRoles, 'categoryKeywords') ? {
            name: 'Category keywords',
            icon: Category,
            route: 'retool',
            parameters: {
              retoolPath: 'category_keywords',
            },
          } : null,
          hasGlobalRole(userRoles, 'updateSpecialPage') ? {
            name: 'Special pages',
            icon: MenuBook,
            route: 'retool',
            parameters: {
              retoolPath: 'special_pages',
            },
          } : null,
          hasGlobalRole(userRoles, 'createNewsletter') ? {
            name: 'Newsletter',
            icon: Email,
            route: 'retool',
            parameters: {
              retoolPath: 'newsletter',
            },
          } : null,
          hasGlobalRole(userRoles, 'removeImage') ? {
            name: 'Lookbook images',
            icon: Image,
            route: 'retool',
            parameters: {
              retoolPath: 'lookbook_images',
            },
          } : null,
          hasGlobalRole(userRoles, 'createAuthor') ? {
            name: 'Authors',
            icon: People,
            route: 'retool',
            parameters: {
              retoolPath: 'create_author',
            },
          } : null,
        ],
      } : null,
    hasGlobalRole(userRoles, 'marketplaceKPI')
    || hasGlobalRole(userRoles, 'jobs')
    || hasGlobalRole(userRoles, 'credits')
    || hasGlobalRole(userRoles, 'orders')
    || hasGlobalRole(userRoles, 'updateNewsArticle') ? {
        name: 'Reports',
        icon: Assessment,
        subItems: [
          hasGlobalRole(userRoles, 'marketplaceKPI') ? {
            name: 'Marketplace KPI',
            icon: Summarize,
            route: 'retool',
            parameters: {
              retoolPath: 'marketplace_kpi',
            },
          } : null,
          hasGlobalRole(userRoles, 'jobs') ? {
            name: 'Published jobs',
            icon: PlaylistAddCheck,
            route: 'retool',
            parameters: {
              retoolPath: 'published_jobs',
            },
          } : null,
          hasGlobalRole(userRoles, 'credits') ? {
            name: 'Used and expiring credits',
            icon: CurrencyExchange,
            route: 'retool',
            parameters: {
              retoolPath: 'used_and_expiring_credits',
            },
          } : null,
          hasGlobalRole(userRoles, 'orders') ? {
            name: 'Orders',
            icon: Payments,
            route: 'retool',
            parameters: {
              retoolPath: 'orders',
            },
          } : null,
          hasGlobalRole(userRoles, 'updateNewsArticle') ? {
            name: 'News articles',
            icon: Article,
            route: 'retool',
            parameters: {
              retoolPath: 'news_articles',
            },
          } : null,
          hasGlobalRole(userRoles, 'jobs') ? {
            name: 'Expiring jobs',
            icon: PlaylistRemove,
            route: 'retool',
            parameters: {
              retoolPath: 'expiring_jobs',
            },
          } : null,
        ],
      } : null,
    hasGlobalRole(userRoles, 'openAI') ? {
      name: 'Translate',
      icon: Translate,
      route: 'retool',
      parameters: {
        retoolPath: 'translate',
      },
    } : null,
    hasGlobalRole(userRoles, 'openAI') ? {
      name: 'Open AI',
      icon: ConnectWithoutContact,
      route: 'retool',
      parameters: {
        retoolPath: 'open_ai',
      },
    } : null,
    hasGlobalRole(userRoles, 'openAI') ? {
      name: 'Image generator',
      icon: ImageSearch,
      route: 'retool',
      parameters: {
        retoolPath: 'image_generator',
      },
    } : null,
    hasGlobalRole(userRoles, 'templates') ? {
      name: 'Templates',
      icon: ViewQuilt,
      route: 'templates',
    } : null,
    hasRoleInAnyGroup(userRoles, 'QRCode') ? {
      name: 'QR code generator',
      icon: QrCode2,
      route: 'retool',
      parameters: {
        retoolPath: 'qr_code_generator',
      },
    } : null,
    hasGlobalRole(userRoles, 'exportInvoices') ? {
      name: 'Finance',
      icon: AccountBalance,
      route: 'retool',
      parameters: {
        retoolPath: 'finance',
      },
    } : null,
  ];

  const items = potentialItems
    .filter(Boolean)
    .map((item) => (Array.isArray(item?.subItems) ? ({
      ...item,
      subItems: item?.subItems.filter(Boolean),
    }) : item)) as DashboardNavigationItem[];

  return [
    {
      name: 'Post a job',
      icon: AddBox,
      route: hasGlobalRole(
        userRoles, 'createJob',
      ) ? 'job-form-admin' : 'job-form',
    },
    ...items,
    {
      name: 'Contact',
      icon: QuestionMark,
      href: 'https://www.fashionunited.info/contact',
      target: '_blank',
    },
  ];
};

export default getDashboardNavigation;
